import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useCart, useCartLine, CartLineQuantity } from "@shopify/hydrogen";
import classNames from "classnames";
import { CartLine } from "@shopify/hydrogen/storefront-api-types";

const CartLineQuantityAdjustButton = React.memo(
  ({
    children,
    adjust,
    className = "",
    onTagsFetched,
  }: {
    children: React.ReactNode;
    adjust: number;
    className?: string;
    onTagsFetched?: (tags: string[]) => void;
  }) => {
    const { linesUpdate } = useCart();
    const { id, merchandise, quantity } = useCartLine() as CartLine;
    const [productTags, setProductTags] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const linesUpdateRef = useRef(linesUpdate);
    linesUpdateRef.current = linesUpdate;

    const fetchData = useCallback(async () => {
      try {
        const response = await fetch("/api/product-id", {
          body: JSON.stringify({ id: merchandise.product.id }),
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        });
        const json = await response.json();

        const tags = json.data.product.tags ?? [];
        setProductTags(tags);
        onTagsFetched?.(tags);
        setIsLoading(false);
      } catch (error) {
        console.log(error);
        setIsLoading(false);
      }
    }, [merchandise.product.id, onTagsFetched]);

    useEffect(() => {
      fetchData();
    }, [fetchData]);

    const handleAdjustQuantity = useCallback(() => {
      const newQuantity = quantity + adjust;

      const hasLimitedEdition = productTags.includes("Limited Edition");
      const hasLimitedTime = productTags.includes("Limited Time");

      if (adjust < 0) {
        if (newQuantity >= 0) {
          linesUpdateRef.current([{ id, quantity: newQuantity }]);
        }
        return;
      }

      let maxQuantity = Infinity;
      if (hasLimitedEdition) maxQuantity = 2;
      if (hasLimitedTime) maxQuantity = 1;

      if (newQuantity > 0 && newQuantity <= maxQuantity) {
        linesUpdateRef.current([{ id, quantity: newQuantity }]);
      }
    }, [quantity, adjust, productTags, id]);

    const isIncreaseDisabled = useMemo(
      () =>
        isLoading ||
        (productTags.includes("Limited Edition") && quantity >= 2) ||
        (productTags.includes("Limited Time") && quantity >= 1),
      [isLoading, productTags, quantity]
    );

    const isDecreaseDisabled = quantity <= 0;

    return (
      <button
        type="button"
        onClick={handleAdjustQuantity}
        disabled={adjust > 0 ? isIncreaseDisabled : isDecreaseDisabled}
        className={classNames(
          "cart-line-adjust-button py-1.5 px-3 flex justify-center items-center transition text-primary/40 hover:text-primary disabled:pointer-events-all disabled:cursor-wait disabled:opacity-50",
          className,
        )}
      >
        {children}
      </button>
    );
  }
);

export const CartLineItem = React.memo(() => {
  const [tags, setTags] = useState<string[]>([]);

  const handleTagsFetched = useCallback((fetchedTags: string[]) => {
    setTags(fetchedTags);
  }, []);

  return (
    <div className="flex items-center overflow-auto border border-black">
      <CartLineQuantityAdjustButton
        adjust={-1}
        className="decrease-quantity"
        onTagsFetched={handleTagsFetched}
      >
        &#8722;
      </CartLineQuantityAdjustButton>
      <CartLineQuantity
        as="div"
        className="border-x px-5 py-1.5 border-solid border-black flex justify-center items-center text-center text-primary/90"
      />
      <CartLineQuantityAdjustButton
        adjust={1}
        className="increase-quantity"
        onTagsFetched={handleTagsFetched}
      >
        &#43;
      </CartLineQuantityAdjustButton>
    </div>
  );
});
